import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Badge,
  Spinner,
  Banner,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Checkbox,
  Select,
  DataTable,
  Modal,
  ProgressBar,
  Tabs,
} from '@shopify/polaris';
import {
  RefreshIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ClockIcon,
  PlayIcon,
} from '@shopify/polaris-icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import AgentStatus from '../components/AgentStatus';
import SyncLogsViewer from '../components/SyncLogsViewer';

interface TallyConfig {
  tallyHost: string;
  tallyPort: number;
  companyName: string;
  autoSyncEnabled: boolean;
  syncOnOrderCreate: boolean;
  syncOnOrderUpdate: boolean;
  syncSchedule: string | null;
  retryAttempts: number;
  retryDelayMs: number;
}

interface SyncStats {
  orders: {
    total: number;
    synced: number;
    pending: number;
    failed: number;
  };
  queue: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
}

interface OrderSyncStatus {
  shopifyOrderId: string;
  orderNumber: string;
  status: 'pending' | 'success' | 'failed';
  tallyVoucherNumber: string | null;
  errorMessage: string | null;
  syncedAt: string | null;
}

const SyncDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const tabs = [
    {
      id: 'overview',
      content: 'Overview',
      accessibilityLabel: 'Sync Overview',
      panelID: 'overview-panel',
    },
    {
      id: 'agent',
      content: 'Tally Agent',
      accessibilityLabel: 'Tally Sync Agent',
      panelID: 'agent-panel',
    },
    {
      id: 'logs',
      content: 'Agent Logs',
      accessibilityLabel: 'Agent Logs',
      panelID: 'logs-panel',
    },
  ];

  const [config, setConfig] = useState<TallyConfig>({
    tallyHost: 'localhost',
    tallyPort: 9000,
    companyName: '',
    autoSyncEnabled: false,
    syncOnOrderCreate: true,
    syncOnOrderUpdate: true,
    syncSchedule: null,
    retryAttempts: 3,
    retryDelayMs: 2000,
  });

  // Fetch Tally config
  const { isLoading: configLoading } = useQuery(
    'tallyConfig',
    () => api.sync.getTallyConfig(),
    {
      onSuccess: (response) => {
        if (response.data.data) {
          setConfig(response.data.data);
        }
      },
    }
  );

  // Fetch sync stats
  const { data: statsData, isLoading: statsLoading } = useQuery(
    'syncStats',
    () => api.sync.getStats(),
    {
      refetchInterval: 5000, // Refresh every 5 seconds
    }
  );

  // Fetch recent sync logs
  const { data: logsData, isLoading: logsLoading } = useQuery(
    'syncLogs',
    () => api.sync.getOrdersSyncStatus({ limit: 10 }),
    {
      refetchInterval: 10000,
    }
  );

  // Fetch queue status
  const { data: queueData, isLoading: queueLoading } = useQuery(
    'queueStatus',
    () => api.sync.getQueueStatus(),
    {
      refetchInterval: 5000,
    }
  );

  // Save config mutation
  const saveConfigMutation = useMutation(
    (newConfig: TallyConfig) => api.sync.saveTallyConfig(newConfig),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tallyConfig');
        setShowConfigModal(false);
      },
    }
  );

  // Trigger manual sync mutation
  const triggerSyncMutation = useMutation(
    () => api.sync.triggerManualSync(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('syncStats');
        queryClient.invalidateQueries('syncLogs');
        queryClient.invalidateQueries('queueStatus');
      },
    }
  );

  // Retry failed mutation
  const retryFailedMutation = useMutation(
    () => api.sync.retryFailed(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('syncStats');
        queryClient.invalidateQueries('syncLogs');
        queryClient.invalidateQueries('queueStatus');
      },
    }
  );

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const response = await api.sync.testTallyConnection({
        tallyHost: config.tallyHost,
        tallyPort: config.tallyPort,
        companyName: config.companyName,
      });
      setConnectionResult({
        success: response.data.success,
        message: response.data.message,
      });
    } catch (error: any) {
      setConnectionResult({
        success: false,
        message: error.response?.data?.message || 'Connection test failed',
      });
    }
    setTestingConnection(false);
  };

  const stats: SyncStats = statsData?.data?.data || {
    orders: { total: 0, synced: 0, pending: 0, failed: 0 },
    queue: { waiting: 0, active: 0, completed: 0, failed: 0, delayed: 0 },
  };

  const logs: OrderSyncStatus[] = logsData?.data?.data?.orders || [];
  const queueStatus = queueData?.data?.data || { stats: {}, recentFailures: [] };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { tone: 'success' | 'critical' | 'warning'; icon: any }> = {
      success: { tone: 'success', icon: CheckCircleIcon },
      failed: { tone: 'critical', icon: AlertCircleIcon },
      pending: { tone: 'warning', icon: ClockIcon },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Badge tone={config.tone} icon={config.icon}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const scheduleOptions = [
    { label: 'Disabled', value: '' },
    { label: 'Every 15 minutes', value: 'every_15_min' },
    { label: 'Every 30 minutes', value: 'every_30_min' },
    { label: 'Hourly', value: 'hourly' },
    { label: 'Every 6 hours', value: 'every_6_hours' },
    { label: 'Daily at midnight', value: 'daily' },
  ];

  const isLoading = configLoading || statsLoading || logsLoading || queueLoading;

  if (isLoading) {
    return (
      <Page title="Tally Sync Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" gap="200">
                <Spinner size="large" />
                <Text variant="bodyMd" as="span">Loading sync dashboard...</Text>
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const syncedPercentage = stats.orders.total > 0
    ? Math.round((stats.orders.synced / stats.orders.total) * 100)
    : 0;

  const renderOverviewTab = () => (
    <Layout>
        {/* Connection Status Banner */}
        {connectionResult && (
          <Layout.Section>
            <Banner
              title={connectionResult.success ? 'Connection Successful' : 'Connection Failed'}
              tone={connectionResult.success ? 'success' : 'critical'}
              onDismiss={() => setConnectionResult(null)}
            >
              <p>{connectionResult.message}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Tally Configuration Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd" as="h2">Tally Connection</Text>
                <Badge tone={config.autoSyncEnabled ? 'success' : 'attention'}>
                  {config.autoSyncEnabled ? 'Auto-Sync Enabled' : 'Auto-Sync Disabled'}
                </Badge>
              </InlineStack>
              <InlineGrid columns={4} gap="400">
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Host</Text>
                  <Text variant="bodyMd" as="p">{config.tallyHost}:{config.tallyPort}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Company</Text>
                  <Text variant="bodyMd" as="p">{config.companyName || 'Not configured'}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Sync Schedule</Text>
                  <Text variant="bodyMd" as="p">
                    {config.syncSchedule
                      ? scheduleOptions.find(o => o.value === config.syncSchedule)?.label || config.syncSchedule
                      : 'Manual only'}
                  </Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Webhook Sync</Text>
                  <Text variant="bodyMd" as="p">
                    {config.syncOnOrderCreate || config.syncOnOrderUpdate ? 'Enabled' : 'Disabled'}
                  </Text>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Sync Statistics */}
        <Layout.Section>
          <InlineGrid columns={4} gap="400">
            <Card>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Total Orders</Text>
                <Text variant="heading2xl" as="p">{stats.orders.total}</Text>
                <ProgressBar progress={syncedPercentage} tone="primary" size="small" />
                <Text variant="bodySm" tone="subdued" as="span">
                  {syncedPercentage}% synced
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Synced</Text>
                <Text variant="heading2xl" as="p" tone="success">{stats.orders.synced}</Text>
                <Badge tone="success">Successfully synced to Tally</Badge>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Pending</Text>
                <Text variant="heading2xl" as="p" tone="caution">{stats.orders.pending}</Text>
                <Badge tone="warning">Awaiting sync</Badge>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Failed</Text>
                <Text variant="heading2xl" as="p" tone="critical">{stats.orders.failed}</Text>
                <Badge tone="critical">Requires attention</Badge>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Queue Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Queue Status</Text>
              <InlineGrid columns={5} gap="400">
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Waiting</Text>
                  <Text variant="headingLg" as="p">{queueStatus.stats.waiting || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Active</Text>
                  <Text variant="headingLg" as="p" tone="success">{queueStatus.stats.active || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Completed</Text>
                  <Text variant="headingLg" as="p">{queueStatus.stats.completed || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Failed</Text>
                  <Text variant="headingLg" as="p" tone="critical">{queueStatus.stats.failed || 0}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued" as="span">Delayed</Text>
                  <Text variant="headingLg" as="p">{queueStatus.stats.delayed || 0}</Text>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Sync Activity */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Recent Sync Activity</Text>
              {logs.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={['Order #', 'Status', 'Tally Voucher', 'Synced At', 'Error']}
                  rows={logs.map((log) => [
                    log.orderNumber || log.shopifyOrderId,
                    getStatusBadge(log.status),
                    log.tallyVoucherNumber || '-',
                    formatDate(log.syncedAt),
                    log.errorMessage ? (
                      <Text variant="bodySm" tone="critical" as="span">
                        {log.errorMessage.substring(0, 50)}...
                      </Text>
                    ) : '-',
                  ])}
                />
              ) : (
                <Text variant="bodyMd" tone="subdued" as="p">
                  No sync activity yet. Configure Tally and enable auto-sync to start synchronizing orders.
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Failures */}
        {queueStatus.recentFailures && queueStatus.recentFailures.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingMd" as="h2">Recent Failures</Text>
                  <Button
                    onClick={() => retryFailedMutation.mutate()}
                    loading={retryFailedMutation.isLoading}
                  >
                    Retry All
                  </Button>
                </InlineStack>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'numeric']}
                  headings={['Order ID', 'Shop', 'Error', 'Attempts']}
                  rows={queueStatus.recentFailures.map((failure: any) => [
                    failure.orderId,
                    failure.shop,
                    failure.failedReason?.substring(0, 60) || 'Unknown',
                    failure.attemptsMade,
                  ])}
                />
              </BlockStack>
            </Card>
          </Layout.Section>
        )}
      </Layout>
  );

  const renderAgentTab = () => (
    <Layout>
      <Layout.Section>
        <AgentStatus />
      </Layout.Section>
    </Layout>
  );

  const renderLogsTab = () => (
    <Layout>
      <Layout.Section>
        <SyncLogsViewer />
      </Layout.Section>
    </Layout>
  );

  return (
    <Page
      title="Tally Sync Dashboard"
      subtitle="Automatic synchronization of Shopify orders to Tally"
      primaryAction={{
        content: 'Configure Tally',
        onAction: () => setShowConfigModal(true),
      }}
      secondaryActions={[
        {
          content: 'Trigger Manual Sync',
          icon: PlayIcon,
          onAction: () => triggerSyncMutation.mutate(),
          loading: triggerSyncMutation.isLoading,
        },
        {
          content: 'Retry Failed',
          icon: RefreshIcon,
          onAction: () => retryFailedMutation.mutate(),
          loading: retryFailedMutation.isLoading,
          disabled: stats.queue.failed === 0,
        },
      ]}
    >
      <BlockStack gap="400">
        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />

        {selectedTab === 0 && renderOverviewTab()}
        {selectedTab === 1 && renderAgentTab()}
        {selectedTab === 2 && renderLogsTab()}
      </BlockStack>

      {/* Tally Configuration Modal */}
      <Modal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configure Tally Connection"
        primaryAction={{
          content: 'Save Configuration',
          onAction: () => saveConfigMutation.mutate(config),
          loading: saveConfigMutation.isLoading,
        }}
        secondaryActions={[
          {
            content: 'Test Connection',
            onAction: testConnection,
            loading: testingConnection,
          },
          {
            content: 'Cancel',
            onAction: () => setShowConfigModal(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {connectionResult && (
              <Banner
                title={connectionResult.success ? 'Connection Successful' : 'Connection Failed'}
                tone={connectionResult.success ? 'success' : 'critical'}
              >
                <p>{connectionResult.message}</p>
              </Banner>
            )}

            <InlineGrid columns={2} gap="400">
              <TextField
                label="Tally Host"
                value={config.tallyHost}
                onChange={(value) => setConfig({ ...config, tallyHost: value })}
                placeholder="localhost"
                autoComplete="off"
              />
              <TextField
                label="Tally Port"
                type="number"
                value={config.tallyPort.toString()}
                onChange={(value) => setConfig({ ...config, tallyPort: parseInt(value) || 9000 })}
                placeholder="9000"
                autoComplete="off"
              />
            </InlineGrid>

            <TextField
              label="Company Name"
              value={config.companyName}
              onChange={(value) => setConfig({ ...config, companyName: value })}
              placeholder="Enter your Tally company name"
              helpText="The company in Tally where vouchers will be created"
              autoComplete="off"
            />

            <Checkbox
              label="Enable Auto-Sync"
              checked={config.autoSyncEnabled}
              onChange={(checked) => setConfig({ ...config, autoSyncEnabled: checked })}
              helpText="Automatically sync orders to Tally"
            />

            {config.autoSyncEnabled && (
              <>
                <Checkbox
                  label="Sync on Order Create"
                  checked={config.syncOnOrderCreate}
                  onChange={(checked) => setConfig({ ...config, syncOnOrderCreate: checked })}
                  helpText="Automatically sync new orders via webhook"
                />

                <Checkbox
                  label="Sync on Order Update"
                  checked={config.syncOnOrderUpdate}
                  onChange={(checked) => setConfig({ ...config, syncOnOrderUpdate: checked })}
                  helpText="Re-sync orders when they are updated"
                />

                <Select
                  label="Scheduled Sync"
                  options={scheduleOptions}
                  value={config.syncSchedule || ''}
                  onChange={(value) => setConfig({ ...config, syncSchedule: value || null })}
                  helpText="Run sync on a schedule in addition to webhooks"
                />
              </>
            )}

            <InlineGrid columns={2} gap="400">
              <TextField
                label="Retry Attempts"
                type="number"
                value={config.retryAttempts.toString()}
                onChange={(value) => setConfig({ ...config, retryAttempts: parseInt(value) || 3 })}
                helpText="Number of retry attempts for failed syncs"
                autoComplete="off"
              />
              <TextField
                label="Retry Delay (ms)"
                type="number"
                value={config.retryDelayMs.toString()}
                onChange={(value) => setConfig({ ...config, retryDelayMs: parseInt(value) || 2000 })}
                helpText="Delay between retries in milliseconds"
                autoComplete="off"
              />
            </InlineGrid>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default SyncDashboard;
